import { LIFECYCLE_GROUPS } from '@features/lifecycles/lifecycle-groups.data';
import { formatSbefSummary } from '@features/sbef/sbef-summary.helpers';
import { getLifecycleLabel } from '@shared/helpers/lifecycles.helpers';
import type { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import { Lifecycle } from '@shared/models/lifecycles.model';

export type ScopeSummaryParts = {
  lifecycle: string;
  sbef?: string;
};

/**
 * Builds the lifecycle and optional SBEF fragments for the scope trigger.
 * Join them visually with the shared middle-dot separator.
 */
export const buildScopeSummaryParts = (
  selectedLifecycles: Lifecycle[],
  lifetime: number | undefined,
  selectedSbefGroups: readonly SbefScopeGroupKey[],
  sbefFilterActive: boolean,
): ScopeSummaryParts => {
  const groupParts = LIFECYCLE_GROUPS.flatMap((group) => {
    const activeLifecycles = group.toggleableLifecycles.filter((lc) =>
      selectedLifecycles.includes(lc),
    );
    if (activeLifecycles.length === 0) return [];
    const phases = getLifecycleLabel(activeLifecycles);
    const groupName =
      group.id === 'operation' && lifetime !== undefined
        ? `Operation ${lifetime}yr`
        : group.label;
    return [`${groupName} ${phases}`];
  });

  const lifecycle = groupParts.join(', ');
  if (!sbefFilterActive) return { lifecycle };
  return {
    lifecycle,
    sbef: `SBEF ${formatSbefSummary(selectedSbefGroups)}`,
  };
};

/**
 * Plain-text scope summary for non-trigger surfaces (e.g. the report cover).
 */
export const buildScopeSummaryLabel = (
  selectedLifecycles: Lifecycle[],
  lifetime: number | undefined,
  selectedSbefGroups: readonly SbefScopeGroupKey[],
  sbefFilterActive: boolean,
): string => {
  const { lifecycle, sbef } = buildScopeSummaryParts(
    selectedLifecycles,
    lifetime,
    selectedSbefGroups,
    sbefFilterActive,
  );
  return [lifecycle, sbef].filter(Boolean).join(' ');
};
